import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { formatPrice } from '../../utils/price';
import { Spinner } from '../common/Spinner';
import { CartProvider, useCartContext } from './CartProvider';
import { LineItem } from './LineItem';

interface CartProps {
    locale: string;
    checkoutUrl: string;
    standalone: boolean;
}

export const Cart = ({ locale, checkoutUrl, standalone }: CartProps) => {
    const { t } = useTranslation();
    const { cart, errors, message, updatingItems, updateQuantity } = useCartContext();

    useEffect(() => {
        i18n.changeLanguage(locale);
    }, [locale]);

    if (!cart) {
        return (
            <div className="cart-container max-w-screen-lg mx-auto">
                <div className="flex justify-center items-center py-12">
                    <Spinner color="green" />
                </div>
            </div>
        );
    }

    return (
        <ErrorBoundary fallback={<div>{t('common.error')}</div>}>
            <div className="cart-container max-w-screen-lg mx-auto">
                {message && Object.keys(errors).length === 0 && (
                    <div className="h-[52px]">
                        <div
                            className={`
                            my-4
                            bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative
                            transition-opacity duration-200
                            ${message && Object.keys(errors).length === 0 ? 'opacity-100' : 'opacity-0'}
                        `}
                            role="alert"
                        >
                            <span className="block sm:inline">{message}</span>
                        </div>
                    </div>
                )}

                {cart?.items?.length ? (
                    <div className="space-y-4 mt-4">
                        {cart.items.map((item) => (
                            <LineItem
                                key={item.id}
                                item={item}
                                isUpdating={updatingItems.includes(item.id)}
                                errors={errors}
                                onUpdateQuantity={updateQuantity}
                                locale={locale}
                                standalone={standalone}
                            />
                        ))}
                        <div className="mt-4 p-4 border-t">
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span>{t('cart.subtotal')}:</span>
                                    <span>{formatPrice(cart.totals.itemTotal, locale)}</span>
                                </div>

                                {cart.discounts
                                    .filter((discount) => !discount.included)
                                    .map((discount) => (
                                        <div key={discount.code} className="flex justify-between">
                                            <span>
                                                {t('voucher.prefix', {
                                                    description: discount.code,
                                                })}
                                            </span>
                                            <span>{formatPrice(discount.discount, locale)}</span>
                                        </div>
                                    ))}

                                <div className="flex justify-between font-medium pt-2 border-t">
                                    <span>{t('cart.total')}:</span>
                                    <span className="text-2xl">
                                        {formatPrice(cart.totals.total, locale)}
                                    </span>
                                </div>

                                {cart.totals.totalTaxIncluded > 0 && (
                                    <div className="flex justify-between text-gray-600">
                                        <span>{t('cart.vat')}:</span>
                                        <span>
                                            {formatPrice(cart.totals.totalTaxIncluded, locale)}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {standalone && (
                                <div className="mt-4">
                                    <a
                                        href={checkoutUrl}
                                        className="block w-full py-2 px-4 text-center btn"
                                    >
                                        {t('cart.checkout')}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p>{t('cart.empty')}</p>
                    </div>
                )}
            </div>
        </ErrorBoundary>
    );
};

// Initialize all cart components
const cartElements = document.querySelectorAll('.cart-component') as NodeListOf<HTMLElement>;
cartElements.forEach((element) => {
    const root = createRoot(element);

    try {
        const locale = element.dataset.locale as string;
        const checkoutUrl = element.dataset.checkoutUrl as string;

        root.render(
            <CartProvider locale={locale}>
                <Cart locale={locale} checkoutUrl={checkoutUrl} standalone={true} />
            </CartProvider>
        );
    } catch (error) {
        console.error('Failed to initialize cart:', error);
    }
});
